import React, { useState, useId, useRef, useMemo } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Typography } from 'design-system/src/components/Typography/Typography';
import toast from 'react-hot-toast';
import { Toast } from 'design-system/src/components/Toast/Toast';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import { shortErrorCopy } from 'design-system/src/sharedCopy';
import Button from 'design-system/src/components/Button/Button';
import { client } from '../../api';
import SaveFiltersModal from '../SaveFiltersModal/SaveFiltersModal';
import { SaveFiltersButton } from 'design-system/src/components/SaveFiltersButton/SaveFiltersButton';
import { areStringArraysEqual } from '../../utils';
import { priorAuthReviewsQueryKey } from '../../queries';
import {
  sortOfficeNames,
  getDropdownInitialValueFromSearchParams,
  getDropdownInitialValueFromSearchParamsAndCookie,
  getInitialTableColumnFilterValueFromCookie,
  updateTableColumnFilterValueCookie,
  priorAuthReviewsFilterValuesFromSearchParams,
  updateUrlSearchParams,
  getPrescriberFilterValue,
} from '../../utils';
import { formatStandardDateLocaleString, formatStandardDate } from 'shared-utils/utils/formatDate';
import { MULTI_SELECT_DELINEATOR } from '../../constants';
import { PriorAuthSubmitModal } from './PriorAuthSubmitModal';
import { AuthContext } from '../../auth';
import { useDefaultFilters } from '../../hooks/useDefaultFilters';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
  ColumnFiltersState,
  VisibilityState,
  getFilteredRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';

import type { Row } from '@tanstack/react-table';

import type { PriorAuthReviewSummary, PriorAuthReviewStatus } from '../../api';
import { TableMultiselectFilter } from 'design-system/src/components/TableDropdownFilter/TableDropdownFilter';
import {
  type MultiselectAutocompleteOption,
  MultiselectAutocompleteHandle,
} from 'design-system/src/components/MultiselectAutocomplete/MultiselectAutocomplete';
import { useFetchPortalTableFilters } from '../useFetchPortalTableFilters';

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ClickableRow,
  FilterBar,
  FilterBarRow,
  TabsAndSearchRow,
  Table,
  TableCell,
  TableContainer,
  TableHeader,
  HeaderSortingIndicator,
  TableTabList,
  TableResultCount,
  DividerPipe,
  NoPaddingCheckbox,
  SearchResultsBanner,
  DateCellWithStatus,
} from '../Table';
import { TableSearchWithPreview } from '../TableSearchWithPreview';
import { PriorAuthReviewPreviewItem } from './PriorAuthReviewPreviewItem';
import { filterPriorAuthReview } from '../pas/priorAuthSearchUtils';
import FormattedCell from 'design-system/src/components/FormattedCell/FormattedCell';

import TableNullState, { TableNullStateType } from '../TableNullState/TableNullState';
import { useTableNullState } from '../../hooks/useTableNullState';
import { createTrackingProps } from 'shared-utils/analytics';

type UITaskStatus = 'Open' | 'Reviewed';

const columnHelper = createColumnHelper<PriorAuthReviewSummary>();

interface PriorAuthReviewTableProps {
  data: PriorAuthReviewSummary[];
}

const priorAuthReviewDateSortingFn = (
  a: Row<PriorAuthReviewSummary>,
  b: Row<PriorAuthReviewSummary>,
  columnId: string,
) => {
  return new Date(b.getValue(columnId)).getTime() - new Date(a.getValue(columnId)).getTime();
};

const priorAuthReviewPatientSortingFn = (
  a: Row<PriorAuthReviewSummary>,
  b: Row<PriorAuthReviewSummary>,
  columnId: string,
) => {
  const aValue = a.getValue(columnId) as { name: string; dob: string };
  const bValue = b.getValue(columnId) as { name: string; dob: string };
  return bValue.name.localeCompare(aValue.name);
};

const priorAuthReviewDrugSortingFn = (
  a: Row<PriorAuthReviewSummary>,
  b: Row<PriorAuthReviewSummary>,
  columnId: string,
) => {
  const aValue = a.getValue(columnId) as { name: string; dateReceived: string };
  const bValue = b.getValue(columnId) as { name: string; dateReceived: string };
  return bValue.name.localeCompare(aValue.name);
};

const priorAuthReviewTableMultiSelectFilter = (
  row: Row<PriorAuthReviewSummary>,
  columnId: string,
  filterValue: string[],
) => {
  if (!filterValue.length) {
    // No filters are applied; return all rows
    return true;
  }

  // @ts-expect-error columnId will be one of the column heading keys
  return filterValue.includes(row.original[columnId]);
};

const getDefaultNullStateType = (status: string) => {
  if (status === 'Reviewed') {
    return TableNullStateType.NO_PRIOR_AUTH_REVIEWS_REVIEWED;
  } else {
    return TableNullStateType.NO_PRIOR_AUTH_REVIEWS_OPEN;
  }
};

const PriorAuthReviewsTable: React.FC<PriorAuthReviewTableProps> = ({ data }) => {
  const navigate = useNavigate();
  const { priorAuthReviewId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const authContext = React.useContext(AuthContext);
  const userEmail = authContext.user?.email;
  const { data: filterOptions } = useFetchPortalTableFilters();
  const statusTabId = useId();
  const statusPanelId = useId();

  // Add search value state for controlled input
  const [searchValue, setSearchValue] = useState(searchParams.get('search') || '');
  const [appliedSearch, setAppliedSearch] = useState(searchParams.get('search') || '');

  // Refs for TableMultiselectFilter components
  const officeFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const prescriberFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const drugFilterRef = useRef<MultiselectAutocompleteHandle>(null);

  const { defaultPrescriberNames } = useDefaultFilters();
  const officeCookieValues = React.useMemo(
    () => getInitialTableColumnFilterValueFromCookie(authContext.user?.member_id || null, 'office_name'),
    [authContext.user?.member_id],
  );
  const drugCookieValues = React.useMemo(
    () => getInitialTableColumnFilterValueFromCookie(authContext.user?.member_id || null, 'drug_name'),
    [authContext.user?.member_id],
  );
  const hasUserClearedFilters = React.useRef(false);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const currentPrescriberFilter = (columnFilters.find((f) => f.id === 'prescriber_name')?.value as string[]) || [];
  const currentPrescriberFilterNpis = currentPrescriberFilter
    .map((name) => {
      const prescriber = filterOptions?.prescribers?.find((p) => p.description === name);
      return prescriber?.value;
    })
    .filter((npi): npi is string => npi !== undefined);
  const showSaveButton = useMemo(() => {
    // Convert defaultPrescriberNames to NPIs for comparison
    const defaultPrescriberNpis =
      defaultPrescriberNames
        ?.map((name) => {
          const prescriber = filterOptions?.prescribers?.find((p) => p.description === name);
          return prescriber?.value;
        })
        .filter((npi): npi is string => npi !== undefined) || [];

    return !areStringArraysEqual(currentPrescriberFilterNpis, defaultPrescriberNpis);
  }, [currentPrescriberFilterNpis, defaultPrescriberNames, filterOptions?.prescribers]);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const statusParam = searchParams.get('status');
  const initialStatus = statusParam === 'Open' || statusParam === 'Reviewed' ? statusParam : 'Open';
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    // These columns are always hidden
    status: false,
    office_name: false,
    // The selection column is only visible when status is 'Open' (because you can't select accepted reviews)
    selection: initialStatus === 'Open',
  });
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [allRowsSelected, setAllRowsSelected] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);

  const deselectAllRows = () => {
    setSelectedRowIds([]);
    setAllRowsSelected(false);
  };

  const getUIPriorAuthReviewStatus = (status: PriorAuthReviewStatus): UITaskStatus => {
    return status === 'submitted' ? 'Reviewed' : 'Open';
  };

  const queryClient = useQueryClient();
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';
  const acceptPriorAuthReview = useMutation<unknown, AxiosError<{ error: string }>, boolean>({
    mutationFn: async (attestationCheckboxIsChecked: boolean) => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking "bulk accept prior auth reviews" for IDs: ${selectedRowIds.join(', ')}`);
        return;
      }
      const res = await client.post(`prior-auth-reviews/bulk/form/submit`, {
        prior_auth_review_ids: selectedRowIds,
        attestation_submitted: attestationCheckboxIsChecked,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.custom(() => (
        <Toast variant="success">{selectedRowIds.length === 1 ? 'Prior auth' : 'Prior auths'} accepted</Toast>
      ));
      setSelectedRowIds([]);
      setAllRowsSelected(false);
      return queryClient.invalidateQueries({ queryKey: priorAuthReviewsQueryKey() });
    },
    onError: (error) => {
      const errorMessage = error.response?.data.error ? error.response?.data.error : shortErrorCopy;
      toast.custom(() => <Toast variant="error">{errorMessage}</Toast>);
      // Invalidate the query on error in case some of the prior auths submitted did succeed
      return queryClient.invalidateQueries({ queryKey: priorAuthReviewsQueryKey() });
    },
  });

  const ToggleAllRowsSelectionButton = (
    <NoPaddingCheckbox
      aria-label={allRowsSelected ? 'unselect all rows' : 'select all rows'}
      checked={allRowsSelected}
      onClick={() => {
        const rowsNotRequiringSignature = table
          .getRowModel()
          .flatRows.filter((row) => !row.original.signature_required);

        if (selectedRowIds.length !== rowsNotRequiringSignature.length) {
          const rowIds = rowsNotRequiringSignature.map((row) => row.original.id);
          setSelectedRowIds(rowIds);
          setAllRowsSelected(true);
        } else {
          setSelectedRowIds([]);
          if (allRowsSelected) {
            setAllRowsSelected(false);
          }
        }
      }}
    />
  );

  const getToggleRowSelectionButton = (row: Row<PriorAuthReviewSummary>) => {
    const isChecked = selectedRowIds.includes(row.original.id);

    if (row.original.signature_required) {
      return (
        <Tooltip
          content="Prior auths that require a signature cannot be accepted from the Prior Auth Reviews page"
          childNotInteractive
        >
          <NoPaddingCheckbox aria-label="select row" checked={false} disabled={true} />
        </Tooltip>
      );
    }

    return (
      <NoPaddingCheckbox
        aria-label={isChecked ? 'unselect row' : 'select row'}
        checked={isChecked}
        onClick={() => {
          const indexOfRowInSelectedRows = selectedRowIds.indexOf(row.original.id);
          if (indexOfRowInSelectedRows !== -1) {
            const updatedSelectedRowIds = [...selectedRowIds];
            updatedSelectedRowIds.splice(indexOfRowInSelectedRows, 1);
            setSelectedRowIds(updatedSelectedRowIds);
            if (allRowsSelected) {
              setAllRowsSelected(false);
            }
          } else {
            const updatedSelectedRowIds = [...selectedRowIds, row.original.id];
            setSelectedRowIds(updatedSelectedRowIds);
            const rows = table.getRowModel().flatRows;
            if (updatedSelectedRowIds.length === rows.length) {
              setAllRowsSelected(true);
            }
          }
        }}
      />
    );
  };

  const columns = [
    columnHelper.display({
      // @ts-expect-error this is a special column with a special button header
      header: ToggleAllRowsSelectionButton,
      id: 'selection',
      enableSorting: false,
      cell: (info: any) => {
        return getToggleRowSelectionButton(info.row);
      },
      enableGlobalFilter: false,
    }),
    columnHelper.accessor('created_at', {
      header: 'Date',
      sortingFn: priorAuthReviewDateSortingFn,
      cell: (info) => (
        <DateCellWithStatus
          date={info.getValue()}
          statusLabel={appliedSearch && info.row.original.status === 'submitted' ? 'Reviewed' : undefined}
        />
      ),
    }),
    columnHelper.accessor(
      (row) => {
        return { name: row.patient_name, dob: row.patient_dob };
      },
      {
        id: 'patient_info',
        header: 'Patient info',
        sortingFn: priorAuthReviewPatientSortingFn,
        cell: (info) => (
          <FormattedCell
            id={info.row.original.id}
            title={info.getValue().name}
            subtitle={`DOB: ${formatStandardDate(info.getValue().dob)}`}
          />
        ),
      },
    ),
    columnHelper.accessor(
      (row) => {
        return {
          name: row.drug_name,
          dateReceived: row.prescription_recieved_at,
        };
      },
      {
        id: 'drug',
        header: 'Drug',
        sortingFn: priorAuthReviewDrugSortingFn,
        filterFn: (row, _, filterValue) => {
          if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
          // drug_description requires special handling because we're looking for a substring match
          // whereas the other columns are looking for an exact match
          const drugName = row.original.drug_name?.toLowerCase() || '';
          return filterValue.some((filterVal) => drugName.includes(filterVal.toLowerCase()));
        },
        cell: (info) => (
          <FormattedCell
            id={info.row.original.id}
            title={info.getValue().name}
            subtitle={`Prescribed ${formatStandardDateLocaleString(info.getValue().dateReceived)}`}
          />
        ),
      },
    ),
    columnHelper.accessor('prescriber_name', {
      header: 'Prescriber',
      filterFn: priorAuthReviewTableMultiSelectFilter,
    }),

    // these columns are only used for filtering, not displaying
    columnHelper.accessor((row) => getUIPriorAuthReviewStatus(row.status), {
      id: 'status',
      header: 'Status',
      enableGlobalFilter: false,
      filterFn: (row, _, filterValue) => {
        // 'all' bypasses filtering - used when search is active to show results across tabs
        if (filterValue === 'all') {
          return true;
        }
        return getUIPriorAuthReviewStatus(row.original.status) === filterValue;
      },
    }),
    columnHelper.accessor('office_name', {
      header: 'Office',
      enableGlobalFilter: false,
      filterFn: priorAuthReviewTableMultiSelectFilter,
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: {
      columnFilters,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    enableGlobalFilter: true,
    // Use filterPriorAuthReview for consistent fuzzy date and name format matching
    globalFilterFn: (row, _, filterValue) => {
      if (!filterValue || typeof filterValue !== 'string') return true;
      return filterPriorAuthReview(row.original, filterValue);
    },
    onColumnVisibilityChange: setColumnVisibility,
    initialState: {
      globalFilter: searchValue,
    },
  });

  React.useEffect(() => {
    const searchFromParams = searchParams.get('search') || '';
    setSearchValue(searchFromParams);
    setAppliedSearch(searchFromParams);
    table.setGlobalFilter(searchFromParams);

    const filterValues = priorAuthReviewsFilterValuesFromSearchParams(searchParams);

    const prescriberValue = getPrescriberFilterValue(
      filterValues,
      filterOptions,
      defaultPrescriberNames,
      hasUserClearedFilters,
    );

    // When search is active, show results across all statuses
    const statusFilterValue = searchFromParams ? 'all' : searchParams.get('status') || 'Open';
    setColumnFilters([
      { id: 'status', value: statusFilterValue },
      {
        id: 'office_name',
        value: filterValues.office.length
          ? filterValues.office
          : hasUserClearedFilters.current
            ? []
            : officeCookieValues,
      },
      {
        id: 'prescriber_name',
        value: prescriberValue,
      },
      {
        id: 'drug',
        value: filterValues.drug_name.length
          ? filterValues.drug_name
          : hasUserClearedFilters.current
            ? []
            : drugCookieValues,
      },
    ]);
  }, [searchParams, table, userEmail, defaultPrescriberNames, filterOptions, officeCookieValues, drugCookieValues]);

  // Sync columnVisibility.selection with URL status changes (e.g., browser back/forward)
  React.useEffect(() => {
    const newSelectionValue = (searchParams.get('status') || 'Open') === 'Open';
    setColumnVisibility((prev) => {
      if (prev.selection !== newSelectionValue) {
        return {
          ...prev,
          selection: newSelectionValue,
        };
      }
      return prev;
    });
  }, [searchParams]);

  const setStatus = React.useCallback(
    (status: string, e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
      }
      updateUrlSearchParams(searchParams, setSearchParams, { status });
      table.getColumn('status')!.setFilterValue(status);
    },
    [searchParams, setSearchParams, table],
  );

  const nullStateType = useTableNullState({
    columnFilters,
    filterIds: ['office_name', 'prescriber_name', 'drug'],
    defaultNullStateType: getDefaultNullStateType(table.getColumn('status')!.getFilterValue() as string),
    globalFilter: searchValue,
  });

  const clearSearch = React.useCallback(() => {
    setAppliedSearch('');
    setSearchValue('');
    table.setGlobalFilter('');
    updateUrlSearchParams(searchParams, setSearchParams, {
      search: undefined,
    });
  }, [table, searchParams, setSearchParams]);

  const clearAllFilters = React.useCallback(() => {
    // Clear all column filters except status (which is controlled by tabs)
    const currentStatus = searchParams.get('status') || 'Open';
    setColumnFilters([{ id: 'status', value: currentStatus }]);

    // Clear global filter and search input
    table.setGlobalFilter('');
    setSearchValue('');
    setAppliedSearch('');

    // Clear cookies for saved filter values
    updateTableColumnFilterValueCookie([], authContext.user?.member_id || null, 'office_name');
    updateTableColumnFilterValueCookie([], authContext.user?.member_id || null, 'drug_name');

    // Clear TableMultiselectFilter refs
    officeFilterRef.current?.clear();
    prescriberFilterRef.current?.clear();
    drugFilterRef.current?.clear();

    // Mark that user has explicitly cleared filters
    hasUserClearedFilters.current = true;

    updateUrlSearchParams(searchParams, setSearchParams, {
      search: undefined,
      office: undefined,
      prescriber: undefined,
      drug_name: undefined,
    });
  }, [table, searchParams, setSearchParams, authContext.user?.member_id]);

  return (
    <>
      <TableContainer>
        <FilterBar>
          <TabsAndSearchRow>
            <TableTabList
              tabs={[{ value: 'Open' }, { value: 'Reviewed' }]}
              selectedValue={appliedSearch ? '' : (table.getColumn('status')!.getFilterValue() as string) || 'Open'}
              onSelect={(value) => {
                // Clear search when switching tabs
                if (appliedSearch) {
                  setAppliedSearch('');
                  setSearchValue('');
                  table.setGlobalFilter('');
                  updateUrlSearchParams(searchParams, setSearchParams, {
                    search: undefined,
                    status: value,
                  });
                } else {
                  setStatus(value);
                }
                setColumnVisibility((prev) => ({
                  ...prev,
                  selection: value === 'Open',
                }));
              }}
              tabListId={statusTabId}
              tabPanelId={statusPanelId}
              ariaLabel="Filter prior auth reviews by status"
              {...createTrackingProps({ elementName: 'PA Reviews Table: Status Tabs', unmask: true })}
            />
            <TableSearchWithPreview
              tableName="PA Reviews Table"
              value={searchValue}
              onChange={setSearchValue}
              onApplySearch={(value) => {
                setAppliedSearch(value);
                table.setGlobalFilter(value);
                // Show results across all statuses when search is active
                if (value) {
                  table.getColumn('status')?.setFilterValue('all');
                }
                updateUrlSearchParams(searchParams, setSearchParams, {
                  search: value === '' ? undefined : value,
                });
              }}
              onClear={() => {
                setAppliedSearch('');
                setSearchValue('');
                table.setGlobalFilter('');
                updateUrlSearchParams(searchParams, setSearchParams, {
                  search: undefined,
                });
              }}
              data={data}
              filterFn={filterPriorAuthReview}
              getItemKey={(review) => review.id}
              onPreviewItemClick={(review) => navigate(review.id)}
              renderPreviewItem={(review) => <PriorAuthReviewPreviewItem review={review} searchTerm={searchValue} />}
            />
          </TabsAndSearchRow>

          {appliedSearch ? (
            <SearchResultsBanner
              searchTerm={appliedSearch}
              onClearSearch={clearSearch}
              count={table.getRowModel().rows?.length ?? 0}
            />
          ) : selectedRowIds.length > 0 ? (
            <FilterBarRow>
              <Button
                size="sm"
                onClick={() => setShowAcceptModal(true)}
                {...createTrackingProps({ elementName: 'PA Reviews Table: Bulk Accept Button' })}
              >
                Accept
              </Button>
              <Button size="sm" variant="tertiary" onClick={deselectAllRows}>
                Deselect
              </Button>
              <DividerPipe />
              <Typography styledAs="bodySmallSpaceGrotesk">{selectedRowIds.length} selected</Typography>
              <TableResultCount count={table.getRowModel().rows?.length ?? 0} />
            </FilterBarRow>
          ) : (
            <FilterBarRow>
              <TableMultiselectFilter
                ref={officeFilterRef}
                options={filterOptions?.offices || []}
                label={'Office'}
                initialValue={
                  getDropdownInitialValueFromSearchParamsAndCookie(
                    searchParams.get('office') ||
                      (hasUserClearedFilters.current ? '' : officeCookieValues.join(MULTI_SELECT_DELINEATOR)),
                    authContext.user?.member_id || null,
                    'office_name',
                    filterOptions?.offices,
                  ) as MultiselectAutocompleteOption[]
                }
                onChange={(_, value: MultiselectAutocompleteOption[]) => {
                  const selectedValuesList = value.map((valueItem) => valueItem.value);

                  // Reset the cleared flag when user makes a manual selection
                  hasUserClearedFilters.current = false;

                  table.getColumn('office_name')!.setFilterValue(selectedValuesList);
                  updateTableColumnFilterValueCookie(value, authContext.user?.member_id || null, 'office_name');
                  updateUrlSearchParams(searchParams, setSearchParams, {
                    office: selectedValuesList.length ? selectedValuesList.join(MULTI_SELECT_DELINEATOR) : undefined,
                  });
                }}
                sortFn={sortOfficeNames}
                {...createTrackingProps({ elementName: 'PA Reviews Table: Office Filter', unmask: true })}
              />
              <TableMultiselectFilter
                // Force remount when prescriber options load to ensure initialValue is applied correctly
                key={filterOptions?.prescribers ? 'prescriber-loaded' : 'prescriber-loading'}
                ref={prescriberFilterRef}
                options={filterOptions?.prescribers || []}
                label={'Prescriber'}
                initialValue={
                  filterOptions?.prescribers
                    ? (getDropdownInitialValueFromSearchParams(
                        authContext.user?.default_filters,
                        searchParams.get('prescriber') || '',
                        'providers',
                        filterOptions.prescribers,
                      ) as MultiselectAutocompleteOption[])
                    : []
                }
                onChange={(_, value: MultiselectAutocompleteOption[]) => {
                  const selectedNamesList = value.map((valueItem) => valueItem.description);
                  const selectedNpisList = value.map((valueItem) => valueItem.value);

                  // Reset the cleared flag when user makes a manual selection
                  hasUserClearedFilters.current = false;

                  table.getColumn('prescriber_name')!.setFilterValue(selectedNamesList);
                  updateUrlSearchParams(searchParams, setSearchParams, {
                    prescriber: selectedNpisList.length ? selectedNpisList.join(MULTI_SELECT_DELINEATOR) : undefined,
                  });
                }}
                {...createTrackingProps({ elementName: 'PA Reviews Table: Prescriber Filter', unmask: true })}
              />
              <TableMultiselectFilter
                ref={drugFilterRef}
                options={filterOptions?.drugs || []}
                label={'Drug'}
                initialValue={
                  getDropdownInitialValueFromSearchParamsAndCookie(
                    searchParams.get('drug_name') ||
                      (hasUserClearedFilters.current ? '' : drugCookieValues.join(MULTI_SELECT_DELINEATOR)),
                    authContext.user?.member_id || null,
                    'drug_name',
                    filterOptions?.drugs,
                  ) as MultiselectAutocompleteOption[]
                }
                onChange={(_, value: MultiselectAutocompleteOption[]) => {
                  const selectedValuesList = value.map((valueItem) => valueItem.value);
                  hasUserClearedFilters.current = false;
                  table.getColumn('drug')!.setFilterValue(selectedValuesList);
                  updateTableColumnFilterValueCookie(value, authContext.user?.member_id || null, 'drug_name');
                  updateUrlSearchParams(searchParams, setSearchParams, {
                    drug_name: selectedValuesList.length ? selectedValuesList.join(MULTI_SELECT_DELINEATOR) : undefined,
                  });
                }}
                {...createTrackingProps({ elementName: 'PA Reviews Table: Drug Filter', unmask: true })}
              />
              <SaveFiltersButton
                $visible={showSaveButton}
                onClick={() => setShowSaveModal(true)}
                {...createTrackingProps({ elementName: 'PA Reviews Table: Save Filters Button' })}
              />
              <TableResultCount count={table.getRowModel().rows?.length ?? 0} />
            </FilterBarRow>
          )}
        </FilterBar>

        <Table role="tabpanel" id={statusPanelId} aria-labelledby={statusTabId}>
          <thead {...createTrackingProps({ unmask: true })}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHeader
                      key={header.id}
                      onClick={header.column.getCanSort() ? () => header.column.toggleSorting() : undefined}
                      $sortable={header.column.getCanSort()}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <HeaderSortingIndicator
                          isSorted={header.column.getIsSorted()}
                          sortDirection={header.column.getIsSorted()}
                        />
                      )}
                    </TableHeader>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <ClickableRow
                  $selected={row.original.id === priorAuthReviewId}
                  $new={row.original.status === 'unreviewed'}
                  key={row.original.id}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      onClick={cell.column.id === 'selection' ? undefined : () => navigate(row.original.id)}
                      onKeyDown={
                        cell.column.id === 'selection'
                          ? undefined
                          : (event: React.KeyboardEvent<HTMLTableCellElement>) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                navigate(row.original.id);
                              }
                            }
                      }
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </ClickableRow>
              ))
            ) : (
              <tr>
                <TableCell colSpan={columns.length}>
                  <TableNullState
                    nullStateType={nullStateType}
                    onClearFilters={clearAllFilters}
                    onClearSearch={clearSearch}
                    onAction={() => navigate('/settings?tab=notifications')}
                    tableName="Prior Auth Reviews Table"
                  />
                </TableCell>
              </tr>
            )}
          </tbody>
        </Table>
      </TableContainer>

      {showAcceptModal && !!selectedRowIds.length ? (
        <PriorAuthSubmitModal
          onClose={() => setShowAcceptModal(false)}
          onSubmit={(checked) => acceptPriorAuthReview.mutate(checked)}
          isSubmitting={acceptPriorAuthReview.isPending}
        />
      ) : null}

      {showSaveModal && (
        <SaveFiltersModal
          onClose={() => setShowSaveModal(false)}
          filters={[
            { label: 'Prescriber', displayValues: currentPrescriberFilter, saveValues: currentPrescriberFilterNpis },
          ]}
        />
      )}
    </>
  );
};

export default PriorAuthReviewsTable;
